// typescript
import React, { useEffect, useMemo, useState } from "react";
import { Localizer } from "@/main/react/Types";
import {
    AjaxSeriesRestControllerApi,
    SeriesDTO,
    SeriesToAddDiscussionTo,
    DiscussionWithoutContentDTO
} from "@/generated-client";
import { ClientFactory } from "@/main/common/ClientFactory";
import { getSafeToastMessageFromError } from "@/main/common/ApiUtils";
import {
    type DiscussionDraftPublishTargetCounts,
    type DraftSeriesToAddDiscussionToDTO,
} from "@/main/common/DiscussionDraftPublishTargetsApi";
// ...existing code... (MetaHandler removed because it's not used in this file)
import { ListPlus, Bookmark, Search } from "lucide-react";
import { emitDiscussionAddedToSeries, emitDiscussionRemovedFromSeries, emitSeriesAddedToDiscussion, emitSeriesRemovedFromDiscussion } from "@/main/common/EventRegistry";
import { toast } from "sonner";
import DateFormatter from "@/main/common/DateFormatter";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import { Spinner } from "@/components/ui/spinner";
import SimpleDialog, { openSimpleDialog } from "@/main/react/Dialog/SimpleDialog";
import { showCreateSeriesDialog } from "@/main/react/Dialog/UpdateSeriesSettingsDialog";
import logger from "@/lib/logger";
import { PaginatedListEmptyState } from "@/main/react/list/PaginatedListState";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import SensitiveMediaGuard from "@/main/react/sensitive/SensitiveMediaGuard";
import { getHideSensitiveMediaPreference, getSensitivePreferenceMode } from "@/main/common/SensitivePreference";
import { getUserFacingSeriesDescription, getUserFacingSeriesTitle } from "@/main/react/series/BookmarksSeriesLocalization";
import { getMaskedContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import { getDisplayUsername, getUserProfilePath } from "@/main/common/UsernameDisplayUtils";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";

type SharedProps = {
    onClose?: () => void;
    onCreateSeries?: () => void;
    localizer: Localizer;
};

type LiveDiscussionProps = SharedProps & {
    mode?: "liveDiscussion";
    discussionPublicId: string;
};

type DraftPublishTargetProps = SharedProps & {
    mode: "draftPublishTargets";
    draftPublicId: string;
    onPublishTargetCountsChange?: (counts: DiscussionDraftPublishTargetCounts) => void;
};

type Props = LiveDiscussionProps | DraftPublishTargetProps;
type SeriesEntry = SeriesToAddDiscussionTo | DraftSeriesToAddDiscussionToDTO;

function isDraftPublishTargetMode(props: Props): props is DraftPublishTargetProps {
    return props.mode === "draftPublishTargets";
}

export function AddDiscussionToSeriesDialogInner(props: Props) {
    const { onCreateSeries, localizer } = props;
    const isDraftMode = isDraftPublishTargetMode(props);
    const discussionPublicId = !isDraftMode ? props.discussionPublicId : undefined;
    const draftPublicId = isDraftMode ? props.draftPublicId : undefined;
    const [loading, setLoading] = useState(true);
    const [series, setSeries] = useState<SeriesEntry[] | null>(null);
    const [query, setQuery] = useState("");
    const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

    const apiClient: AjaxSeriesRestControllerApi = useMemo(
        () => ClientFactory.getAjaxSeriesRestControllerApi(),
        []
    );
    const discussionClient = useMemo(
        () => ClientFactory.getAjaxDiscussionRestControllerApi(),
        []
    );
    const draftPublishTargetsApi = useMemo(
        () => ClientFactory.getDiscussionDraftPublishTargetsApi(),
        []
    );

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setQuery("");

        const loadRequest = isDraftMode
            ? draftPublishTargetsApi.loadDraftSeriesToAddDiscussionTo(draftPublicId ?? "")
            : discussionClient.loadSeriesToAddDiscussionTo(discussionPublicId ?? "", { headers: { Accept: "application/json" } })
                .then((response) => response.data ?? []);

        loadRequest
            .then((response) => {
                if (!mounted) return;
                setSeries(response);
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                logger.error("Failed loading series JSON", err);
                toast.error(localizer("series.modal.toast_error"));
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [discussionPublicId, draftPublicId, draftPublishTargetsApi, isDraftMode, localizer, apiClient, discussionClient]);

    const startToggling = (id: string) => setTogglingIds(prev => new Set(prev).add(id));
    const stopToggling = (id: string) => setTogglingIds(prev => { const n = new Set(prev); n.delete(id); return n; });

    async function toggleSeriesEntry(entry: SeriesToAddDiscussionTo) {
        if (loading || !entry.publicId) return;
        const seriesId = entry.publicId;
        if (togglingIds.has(seriesId)) return;

        startToggling(seriesId);
        try {
            const response = entry.isDiscussionAdded
                ? await apiClient.removeDiscussionFromSeries(seriesId, discussionPublicId!)
                : await apiClient.addDiscussionToSeries(seriesId, discussionPublicId!);
            const updatedSeries = response?.data as (SeriesDTO & { cardMediaItems?: string | null; imageUrls?: string | null }) | undefined;

            setSeries((prev) => {
                if (!prev) return prev;
                return prev.map((s) =>
                    s.publicId === seriesId
                        ? {
                            ...s,
                            ...(updatedSeries ?? {}),
                            isDiscussionAdded: !entry.isDiscussionAdded,
                            discussionsCount: Math.max(
                                0,
                                Number(updatedSeries?.discussionsCount ?? s.discussionsCount ?? 0) + (updatedSeries?.discussionsCount == null ? (entry.isDiscussionAdded ? -1 : 1) : 0),
                            ),
                        }
                        : s
                );
            });

            const payloadDiscussion: DiscussionWithoutContentDTO = { publicId: discussionPublicId! };

            // Series membership is a field of the discussion page-data response, and
            // ClientFactory.invalidateDiscussionPageData's javadoc says every mutation that changes such
            // a field MUST call it -- naming series membership explicitly. Only the vote and bookmark
            // paths did. Without this, hovering a card for 80ms prefetches and caches its page-data,
            // adding it to a series here mutates initiallyAddedToAnySeries / addedToSeriesCount, and a
            // click through inside the 30s TTL renders the discussion as being in no series with the
            // pre-add count. The TTL caps staleness, not correctness inside the prefetch->click window.
            // (2026-08-04)
            ClientFactory.invalidateDiscussionPageData(discussionPublicId!);

            if (!entry.isDiscussionAdded) {
                emitDiscussionAddedToSeries({
                    seriesPublicId: seriesId,
                    discussion: payloadDiscussion,
                    series: updatedSeries,
                });
                toast.success(localizer("series.modal.toast_added"));
            } else {
                emitDiscussionRemovedFromSeries({
                    seriesPublicId: seriesId,
                    discussion: payloadDiscussion,
                    series: updatedSeries,
                });
                toast.success(localizer("series.modal.toast_removed"));
            }

            const payloadSeries: SeriesDTO = (updatedSeries ?? entry) as SeriesDTO;
            if (!entry.isDiscussionAdded) {
                emitSeriesAddedToDiscussion({
                    discussionPublicId: discussionPublicId!,
                    series: payloadSeries
                });
            } else {
                emitSeriesRemovedFromDiscussion({
                    discussionPublicId: discussionPublicId!,
                    series: payloadSeries
                });
            }

        } catch (err) {
            // eslint-disable-next-line no-console
            logger.error("Failed toggling series/follow state", err);
            toast.error(localizer("series.modal.toast_error"));
        } finally {
            stopToggling(seriesId);
        }
    }

    async function toggleDraftSeriesEntry(entry: DraftSeriesToAddDiscussionToDTO) {
        if (loading || !entry.publicId || !draftPublicId) return;

        const seriesPublicId = entry.publicId;
        const alreadyAdded = Boolean(entry.isDiscussionAdded);
        const selectedForPublish = Boolean(entry.isSelectedForPublish);

        if (togglingIds.has(seriesPublicId) || (alreadyAdded && !selectedForPublish)) {
            return;
        }

        startToggling(seriesPublicId);
        try {
            const counts = selectedForPublish
                ? await draftPublishTargetsApi.removeDraftSeriesTarget(draftPublicId, seriesPublicId)
                : await draftPublishTargetsApi.addDraftSeriesTarget(draftPublicId, seriesPublicId);

            setSeries((prev) => {
                if (!prev) return prev;
                return prev.map((item) =>
                    item.publicId === seriesPublicId
                        ? {
                            ...item,
                            isSelectedForPublish: !selectedForPublish,
                        }
                        : item
                );
            });

            if (isDraftMode) {
                props.onPublishTargetCountsChange?.(counts);
            }
            toast.success(localizer(
                selectedForPublish
                    ? "create.discussion.publish_targets.series.toast_removed"
                    : "create.discussion.publish_targets.series.toast_added"
            ));
        } catch (err) {
            logger.error("Failed toggling draft series publish target", err);
            toast.error(getSafeToastMessageFromError(
                err,
                localizer("create.discussion.publish_targets.series.toast_error"),
            ));
        } finally {
            stopToggling(seriesPublicId);
        }
    }

    const normalizedQuery = useMemo(() => query.trim().toLowerCase(), [query]);
    const visibleSeries = useMemo(() => {
        if (!series) return series;
        if (!normalizedQuery) return series;

        return series.filter((entry) => {
            const displayTitle = getUserFacingSeriesTitle(entry as any, localizer);
            const displayDescription = getUserFacingSeriesDescription(entry as any, localizer);
            const haystack = [displayTitle, displayDescription, entry.ownerUsername]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return haystack.includes(normalizedQuery);
        });
    }, [localizer, normalizedQuery, series]);

    function renderSeriesEntry(entry: SeriesEntry) {
        const draftEntry = entry as DraftSeriesToAddDiscussionToDTO;
        const alreadyAddedLocked = isDraftMode && Boolean(draftEntry.isDiscussionAdded) && !Boolean(draftEntry.isSelectedForPublish);
        const disabled = loading || alreadyAddedLocked;
        return (
                <SeriesAddCardDisplayer
                    key={entry.publicId}
                    series={entry}
                    localizer={localizer}
                    isDiscussionAdded={Boolean(entry.isDiscussionAdded)}
                    isSelectedForPublish={isDraftMode ? Boolean(draftEntry.isSelectedForPublish) : undefined}
                    selectionMode={isDraftMode ? "draft" : "live"}
                    alreadyAddedLocked={alreadyAddedLocked}
                    disabled={disabled}
                    isToggling={Boolean(entry.publicId && togglingIds.has(entry.publicId))}
                    onToggleDiscussionAdded={async () => {
                        if (disabled) return;
                        if (isDraftMode) {
                            await toggleDraftSeriesEntry(entry as DraftSeriesToAddDiscussionToDTO);
                            return;
                        }
                        await toggleSeriesEntry(entry as SeriesToAddDiscussionTo);
                    }}
                />
        );
    }

    const noSeries = !loading && (!series || series.length === 0);
    const noSearchMatches = !loading && Boolean(normalizedQuery) && Boolean(series?.length) && (!visibleSeries || visibleSeries.length === 0);

    return (
        <div className="relative w-full">
            <div className="flex flex-col gap-4 items-start justify-start">
                <div className="relative w-full">
                    <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral" aria-hidden="true" />
                    <input
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={localizer("series.modal.search_placeholder")}
                        aria-label={localizer("series.modal.search_placeholder")}
                        className="w-full rounded-lg border border-border bg-input/70 py-3 pl-10 pr-4 text-sm [@media(pointer:coarse)]:text-base text-text-main outline-none transition-colors placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                        data-playwright-id="discussion-add-to-series-search"
                    />
                </div>

                {loading ? (
                    <div className="w-full flex items-center justify-center py-6">
                        <Spinner className="w-6 h-6 text-neutral" />
                    </div>
                ) : noSeries ? (
                    <PaginatedListEmptyState
                        icon={ListPlus}
                        title={localizer("series.modal.no_series")}
                        description={localizer("series.modal.empty.add_to_series.description")}
                        action={onCreateSeries
                            ? {
                                label: localizer("series.modal.new_series"),
                                onClick: onCreateSeries,
                                icon: ListPlus,
                            }
                            : undefined}
                    />
                ) : noSearchMatches ? (
                    <PaginatedListEmptyState
                        icon={Search}
                        title={localizer("series.modal.search_empty.title")}
                        description={localizer("series.modal.search_empty.description")}
                    />
                ) : (
                    <div className="flex flex-col gap-2 w-full max-h-[70vh] overflow-y-auto no-scrollbar pr-2">
                        {visibleSeries && visibleSeries.map((s) => renderSeriesEntry(s))}
                    </div>
                )}
            </div>
        </div>
    );
}

export function showAddDiscussionToSeriesDialog(discussionPublicId: string, localizer: Localizer) {
    openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={<div id="series-modal-title">{localizer("series.modal.title")}</div>}
            width="xl"
        >
            <AddDiscussionToSeriesDialogInner
                discussionPublicId={discussionPublicId}
                onCreateSeries={() => {
                    onOpenChange(false);
                    if (typeof window !== "undefined") {
                        window.setTimeout(() => {
                            showCreateSeriesDialog(localizer);
                        }, 0);
                    }
                }}
                localizer={localizer}
            />
        </SimpleDialog>
    ));
}

/**
 * Draft-mode series picker launched from the inline publish-settings panel under the preview card.
 * Selections are persisted against the draft (`draftPublicId`) and survive close/reopen and the
 * scheduled-publish handoff, matching the draft-backed publish-target contract.
 */
export function showDraftPublishTargetsSeriesDialog(
    draftPublicId: string,
    localizer: Localizer,
    onPublishTargetCountsChange?: (counts: DiscussionDraftPublishTargetCounts) => void,
) {
    openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={<div id="series-modal-title">{localizer("create.discussion.publish_targets.series.choose")}</div>}
            description={localizer("create.discussion.publish_targets.picker.description")}
            width="xl"
        >
            <AddDiscussionToSeriesDialogInner
                mode="draftPublishTargets"
                draftPublicId={draftPublicId}
                onClose={() => onOpenChange(false)}
                onCreateSeries={() => {
                    onOpenChange(false);
                    if (typeof window !== "undefined") {
                        window.setTimeout(() => {
                            showCreateSeriesDialog(localizer);
                        }, 0);
                    }
                }}
                localizer={localizer}
                onPublishTargetCountsChange={onPublishTargetCountsChange}
            />
        </SimpleDialog>
    ));
}

/* SeriesAddCardDisplayer same as before, only moved under this file */
export function SeriesAddCardDisplayer(props: {
    series: SeriesDTO | SeriesToAddDiscussionTo;
    localizer: Localizer;
    isDiscussionAdded?: boolean;
    isSelectedForPublish?: boolean;
    selectionMode?: "live" | "draft";
    alreadyAddedLocked?: boolean;
    disabled?: boolean;
    isToggling?: boolean;
    onToggleDiscussionAdded?: () => Promise<void> | void;
}) {
    const { series, localizer, isDiscussionAdded, isSelectedForPublish, selectionMode, alreadyAddedLocked, disabled, onToggleDiscussionAdded, isToggling } = props;
    const seriesWithFlags = series as (SeriesDTO | SeriesToAddDiscussionTo) & { sensitive?: boolean | null };
    const createdAt = series?.createdAt;
    const df = new DateFormatter(localizer);
    const isSensitive = Boolean(seriesWithFlags.sensitive);
    const sensitivePreference = getSensitivePreferenceMode();
    const hideSensitiveMedia = getHideSensitiveMediaPreference();
    const displayTitle = getUserFacingSeriesTitle(seriesWithFlags as any, localizer);
    const displayDescription = getUserFacingSeriesDescription(seriesWithFlags as any, localizer);
    const displayOwnerUsername = getDisplayUsername(series.ownerUsername, localizer);
    const ownerProfilePath = getUserProfilePath(series.ownerUsername, "discussions");

    const added = typeof isDiscussionAdded === "boolean" ? isDiscussionAdded : Boolean((series as any)?.isDiscussionAdded);
    const selectedForPublish = Boolean(isSelectedForPublish);
    const active = selectionMode === "draft" ? selectedForPublish : added;

    const handleClick = async (e?: React.MouseEvent<HTMLElement>) => {
        try {
            e?.stopPropagation();
            e?.preventDefault();
        } catch { /* ignore */ }
        if (!onToggleDiscussionAdded || isToggling || disabled) return;
        await onToggleDiscussionAdded();
    };

    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            await handleClick(e as any);
        }
    };

    const borderClass = active
        ? "border border-primary"
        : alreadyAddedLocked
            ? "border border-primary/40"
            // `border-border`, not `border-neutral-200`: the latter is a raw Tailwind palette step with
            // no dark variant at all, so it painted a light-grey hairline on a dark card surface.
            : "border border-border";
    const warningCopy = getMaskedContentWarningCopy(localizer, { isSensitive });

    return (
        <div
            key={series.publicId}
            className={`relative w-full min-w-0 max-w-full rounded-xl p-2 outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 ${borderClass} ${disabled ? "cursor-not-allowed opacity-85" : "cursor-pointer"}`}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={onKeyDown}
            aria-pressed={selectionMode === "draft" ? active : added}
            aria-disabled={disabled}
        >
            <div className="flex min-w-0 gap-2 items-start">
                {series.thumbnailUrl && series.thumbnailUrl.length > 0 ? (
                    <SensitiveMediaGuard
                        isSensitive={isSensitive}
                        hideSensitiveMedia={hideSensitiveMedia}
                        sensitivePreference={sensitivePreference}
                        variant="avatar"
                        overlayStacking="below-link-overlay"
                        badgeLabel={warningCopy.badgeLabel}
                        label={warningCopy.label}
                        helperText={warningCopy.helperText}
                        revealText={warningCopy.revealText}
                        className="rounded-full"
                    >
                        <ProfileImage localizer={localizer}
                            imageUrl={series.thumbnailUrl}
                            username={displayTitle}
                            mode="series"
                            hoverable={false}
                            className={"size-12 shrink-0 sm:size-24"}
                        />
                    </SensitiveMediaGuard>
                ) : null}

                <div className="min-w-0 flex-1">
                    {/* The corner slot is IN FLOW and floated, not `absolute`.
                        It holds either a 20px bookmark glyph or a status chip that is a full localized
                        sentence — "Waiting for group leaders" is ~159px in English and longer in Spanish.
                        While it was `absolute top-2 right-2` nothing reserved its width, and the fixed
                        `pe-24` gutter that stood in for that reservation was a GUESS about a localized
                        string: it reserved 96px against a chip that needs up to ~159px, so the chip
                        painted over the title in every locale, and it spent those 96px of a 229px title
                        on the majority of cards that show only the glyph. Floated, the title's first
                        line wraps around whatever width the chip actually took and later lines recover
                        the full column — nothing is estimated. `relative z-20` is kept: the slot still
                        has to sit above the sensitive-media overlay in the thumbnail column.
                        Pinned across all four pickers by `dialogCardTitleGutter.spec.tsx`. */}
                    <div className="relative z-20 float-right ms-2" data-playwright-id="picker-card-corner-slot">
                        {isToggling ? (
                            <Spinner className="w-5 h-5 text-neutral" />
                        ) : alreadyAddedLocked ? (
                            <span className="rounded-full bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary">
                                {localizer("create.discussion.publish_targets.already_added")}
                            </span>
                        ) : active ? (
                            <Bookmark className="w-5 h-5 text-primary fill-current stroke-0" aria-hidden="true" />
                        ) : (
                            <Bookmark className="w-5 h-5 fill-background1  text-muted" aria-hidden="true" />
                        )}
                    </div>
                    <h4 className="wrap-anywhere font-semibold text-base">
                        <span className="inline-flex flex-wrap items-center gap-2">
                            <span>{displayTitle}</span>
                            {isSensitive ? <SensitiveBadge className="py-0">{localizer("content-warning.badge.sensitive")}</SensitiveBadge> : null}
                        </span>
                    </h4>
                    <AuthorAttribution localizer={localizer}
                        name={displayOwnerUsername}
                        href={ownerProfilePath}
                        imageUrl={series.ownerProfileImageUrl}
                        avatarSeed={series.ownerUsername ?? displayOwnerUsername}
                        details={createdAt ? df.timeSince(createdAt) : null}
                        className="mt-1 text-xs text-muted"
                    />
                    <p className="mt-2 text-sm line-clamp-3 wrap-anywhere">{displayDescription || localizer("series.no_description")}</p>
                    {/* `text-muted-foreground`, not `text-neutral` — the latter is the gray-400
                        border/icon tint and measures ~2.3:1 at 12px on the card surface. */}
                    <div className="text-xs text-muted-foreground mt-2">
                        {series.discussionsCount ?? 0} {localizer("series.discussions")} · {series.privateAccess ? localizer("series.private") : localizer("series.public")}
                    </div>
                </div>
            </div>
        </div>
    );
}
